//
// Copyright 2022 DXOS.org
//

import { useMemo, useSyncExternalStore } from 'react';

import { type Database, type Entity, Filter, Query } from '@dxos/echo';

const EMPTY_ARRAY: never[] = [];

const noop = () => {};

interface UseQueryFn {
  <R extends Query.RecordResult>(resource: Database.Queryable | undefined, query: Query.Query<R>): R[];
  <R extends Query.RecordResult>(resource: Database.Queryable | undefined, filter: Filter.Filter<R>): R[];

  <Q extends Query.Any, O extends Entity.Entity<Query.Type<Q>> = Entity.Entity<Query.Type<Q>>>(
    resource: Database.Queryable | undefined,
    query: Q,
  ): O[];

  <F extends Filter.Any, O extends Entity.Entity<Filter.Type<F>> = Entity.Entity<Filter.Type<F>>>(
    resource: Database.Queryable | undefined,
    filter: F,
  ): O[];
}

/**
 * Create subscription.
 *
 * @param queryOrFilter - The query or filter to apply. Query is memoized based on the AST. No need to call useMemo.
 */
// The overloaded public signature returns `O[]` (entities) or the flat record `R[]` (aggregate
// rows and changes) depending on the query shape; the implementation forwards whatever the query result
// produces, so — as with any implementation of a multi-signature overloaded function type — it is
// typed as `any[]` here rather than picking one of the mutually-incompatible overload return shapes.
export const useQuery: UseQueryFn = (
  resource: Database.Queryable | undefined,
  queryOrFilter: Query.Any | Filter.Any,
): any[] => {
  // Keyed by the AST and rebuilt from it, so a caller's inline filter (a new object every render) reuses
  // the query; the memo reads only its key, which the React Compiler requires to honor it.
  const astKey = JSON.stringify((Filter.is(queryOrFilter) ? Query.select(queryOrFilter) : queryOrFilter).ast);

  const { getObjects, subscribe } = useMemo(() => {
    let queryResult = undefined;
    if (resource) {
      queryResult = resource.query(Query.fromAst(JSON.parse(astKey)));
    }

    let subscribed = false;
    return {
      getObjects: () => (subscribed && queryResult ? queryResult.results : EMPTY_ARRAY),
      subscribe: (cb: () => void) => {
        subscribed = true;
        const unsubscribe = queryResult?.subscribe(cb) ?? noop;
        return () => {
          unsubscribe?.();
          subscribed = false;
        };
      },
    };
  }, [resource, astKey]);

  // https://beta.reactjs.org/reference/react/useSyncExternalStore
  // NOTE: This hook will resubscribe whenever the callback passed to the first argument changes; make sure it is stable.
  const objects = useSyncExternalStore<any[]>(subscribe, getObjects);
  return objects;
};
